import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import { useQuery, useSubscription } from "@apollo/react-hooks";
import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MyHrContractCardItem } from "my-hr/components";
import { useMyHrContractsByEmployee } from "my-hr/hooks";
import {
  MyLeaveLeaveCards,
  MyLeaveNewRequestDialog,
} from "my-leave/components";
import { MY_LEAVE_GET_REQUESTS_BY_CONTRACT, MY_LEAVE_REQUESTS_SUBSCRIPTION } from "my-leave/graphql";
import { myLeaveCoverage } from "my-leave/utils/leave-coverage";
import { OcAvatar } from "oc/components";
import { useOcPolling } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

interface RowType {
  employee: string;
  contract: string;
}

export const MyleaveExaminerDashboardCollapse = ({
  row,
  role,
}: {
  row: RowType;
  role: string;
}) => {
  let thisYear = new Date().getFullYear().toString();
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: row?.employee,
  });

  let selectedContract = contractsData?.find(
    (item: { code: string }) => item.code === row?.contract,
  );

  // Query for all leave requests without year filter for validation
  const { data: allData, refetch: allRefetch } = useQuery(
    MY_LEAVE_GET_REQUESTS_BY_CONTRACT,
    {
      fetchPolicy: "cache-and-network",
      pollInterval: useOcPolling(1000 * 60),
      skip: !row?.contract,
      variables: { contract: row?.contract },
    },
  );

  useSubscription(MY_LEAVE_REQUESTS_SUBSCRIPTION, {
    fetchPolicy: "network-only",
    onData: () => {
      allRefetch();
    },
  });

  let allResultData = allData?.leaveRequestsByContract || [];
  let allResult = _.cloneDeep(allResultData);

  const handleMoreButton = () => {
    history.push(`/my-leave/user-requests/MyLeave/${role}/${row?.contract}`);
  };

  const [showNewItemDialog, setShowNewItemDialog] = useState(false);

  const handleShowNewItemDialog = (newState: boolean) => {
    setShowNewItemDialog(newState);
  };

  return (
    <>
      <Box
        padding="16px"
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
      >
        <Box width={isMobile ? "100%" : "50%"}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent={isMobile ? "center" : "flex-start"}
          >
            <OcAvatar
              employee={row?.employee}
              style={{
                width: "100px",
                height: "100px",
              }}
            />
            {isMobile && <Box height="16px" />}
            <Box width="16px" />
            <Box style={{ color: theme.palette.darkBase.main }}>
              <Typography variant="h6" style={{ marginLeft: "10px" }}>
                {selectedContract?.jobClass?.name}
              </Typography>
              <Box height="4px" />
              <MyHrContractCardItem
                icon="mdi:city"
                label={selectedContract?.company?.name}
              />
              <MyHrContractCardItem
                icon="mdi:home-account"
                label={selectedContract?.workingPlace?.name}
              />
              <MyHrContractCardItem
                icon="mdi:sitemap"
                label={selectedContract?.orgUnit?.name}
              />
              <Box height="32px" />
            </Box>
          </Box>
          <Box
            style={{
              display: "flex",
              justifyContent:
                role === "assigner" || isMobile ? "space-between" : "flex-end",
              alignItems: "center",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            {role === "assigner" && (
              <Button
                variant="outlined"
                color="secondary"
                id="assign-button"
                onClick={() => setShowNewItemDialog(true)}
                disableElevation
                fullWidth={isMobile}
              >
                {t("MY_LEAVE_LEAVE_ASSIGN")}
              </Button>
            )}
            {isMobile && role === "assigner" && <Box height="32px" />}
            <Button
              variant="contained"
              color="primary"
              id="more-button"
              disableElevation
              fullWidth={isMobile ? true : false}
              onClick={handleMoreButton}
            >
              {t("OC_MORE")}
            </Button>
          </Box>
        </Box>
        <Box width="16px" />
        <Box
          display="flex"
          flexDirection="column"
          width={isMobile ? "100%" : "50%"}
          height="100%"
        >
          <MyLeaveLeaveCards
            selectedYear={Number(thisYear)}
            contractCode={row?.contract}
          />
        </Box>
        <Box width="16px" />
      </Box>
      <MyLeaveNewRequestDialog
        onShowDialog={handleShowNewItemDialog}
        showDialog={showNewItemDialog}
        onCoverage={(params: any) => myLeaveCoverage(params, allResult)}
        assignEmployeeCode={row?.employee}
        assignContractCode={row?.contract}
        leaveRequests={allResult}
      />
    </>
  );
};

export default MyleaveExaminerDashboardCollapse;
